<template>
  <main class="container page-wrapper">
    <div
      v-if="affiliate.showMessage && affiliate.message"
      :class="{ 'is-invalid': !affiliate.isValid }"
      class="affiliate"
      v-html="affiliate.message"
      data-testid="affiliate-message"
    />

    <div
      v-if="agent.message && agent.message.length > 0"
      :class="{ 'is-invalid': !agent.isValid }"
      class="agent-message"
      v-html="agent.message"
    />

    <div class="header-text">
      <h1 data-testid="page-title">Get A Price</h1>
      <h2>
        Energy. Broadband. Water. TV.<br />All in one monthly bill, split
        equally.
      </h2>
    </div>
    <form
      autocomplete="on"
      class="form pre-signup-form"
      novalidate
      @submit.prevent="submitForm"
    >
      <WarningPanel
        v-if="errorResponse"
        :msg="errorResponse"
        :severe="true"
        :title="'There\'s a problem...'"
      />

      <fieldset class="form__fieldset">
        <div :class="{ '--has-error': $v.leadType.$error }" class="form-item">
          <label class="form__label">
            <span class="form__label-text">Which Describes You?</span>
            <FormError
              v-if="$v.leadType.$error"
              data-testid="student-lead-error"
              msg="Please select an option"
            />
          </label>
          <div class="form__radiobuttons">
            <label
              :class="{ active: leadType === 'Student' }"
              data-testid="student-lead"
              for="studentLead"
            >
              <input
                id="studentLead"
                v-model="leadType"
                name="leadType"
                required
                type="radio"
                value="Student"
              />
              Student
            </label>
            <label
              :class="{ active: leadType === 'Not a Student' }"
              data-testid="notStudentLead"
              for="notStudentLead"
            >
              <input
                id="notStudentLead"
                v-model="leadType"
                name="leadType"
                required
                type="radio"
                value="Not a Student"
              />
              Not A Student
            </label>
          </div>
        </div>
        <div
          :class="{
            '--has-error': $v.firstName.$error || validationErrors.firstName,
          }"
          class="form-item"
        >
          <label class="form__label" for="first-name">
            <span class="form__label-text">First Name</span>
            <FormError
              v-if="$v.firstName.$error || validationErrors.firstName"
              data-testid="first-name-error"
              :msg="
                validationErrors.firstName
                  ? validationErrors.firstName[0]
                  : 'First name required'
              "
            />
          </label>
          <input
            id="first-name"
            ref="firstNameField"
            v-model.trim="firstName"
            autocomplete="given-name"
            class="form__input"
            data-testid="first-name"
            name="first-name"
            required
            type="text"
            @blur="capitaliseName"
          />
        </div>

        <div
          :class="{
            '--has-error': $v.lastName.$error || validationErrors.lastName,
          }"
          class="form-item"
        >
          <label class="form__label" for="last-name">
            <span class="form__label-text">Last Name</span>
            <FormError
              v-if="$v.lastName.$error || validationErrors.lastName"
              data-testid="last-name-error"
              :msg="
                validationErrors.lastName
                  ? validationErrors.lastName[0]
                  : 'Last name required'
              "
            />
          </label>
          <input
            id="last-name"
            v-model.trim="lastName"
            autocomplete="family-name"
            class="form__input"
            data-testid="last-name"
            name="last-name"
            required
            type="text"
            @blur="capitaliseName"
          />
        </div>

        <email-input
          v-model.trim="emailAddress"
          :v="$v.emailAddress"
          label="Email Address"
          name="email"
          :errorMessage="
            validationErrors.emailAddress
              ? validationErrors.emailAddress[0]
              : ''
          "
        />

        <div
          :class="{
            '--has-error': $v.telNumber.$error || validationErrors.firstName,
          }"
          class="form-item"
        >
          <label class="form__label" for="mobile-number">
            <span class="form__label-text">Mobile Number</span>
            <FormError
              v-if="
                ($v.telNumber.$error && !$v.telNumber.required) ||
                  validationErrors.mobileNumber
              "
              data-testid="phone-number-error"
              :msg="
                validationErrors.mobileNumber
                  ? validationErrors.mobileNumber[0]
                  : 'Mobile number required'
              "
            />
          </label>
          <input
            id="mobile-number"
            v-model.trim="telNumber"
            autocomplete="tel"
            class="form__input"
            data-testid="mobile"
            name="mobile-number"
            required
            type="tel"
          />
        </div>

        <div
          v-if="shouldShowWhatsAppConsent"
          class="form-item whatsapp-consent"
          data-testid="whatsapp-consent"
        >
          <div class="whatsapp-consent__row">
            <input
              id="whatsapp_consent"
              v-model="whatsappConsent"
              name="whatsapp_consent"
              type="checkbox"
              class="form-checkbox__input"
              data-testid="whatsapp-consent-checkbox"
            />
            <label
              class="form-checkbox__label whatsapp-consent__label"
              for="whatsapp_consent"
            >
              <svg
                class="whatsapp-consent__icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M0 23.9999L1.687 17.837C0.645998 16.033 0.0989998 13.988 0.0999998 11.891C0.103 5.33499 5.43799 0 11.993 0C15.174 0.000999998 18.16 1.24 20.406 3.48799C22.6509 5.73599 23.8869 8.72398 23.8859 11.902C23.8829 18.459 18.548 23.7939 11.993 23.7939C10.003 23.7929 8.04198 23.2939 6.30499 22.346L0 23.9999ZM6.59698 20.193C8.27298 21.188 9.87298 21.784 11.989 21.785C17.437 21.785 21.875 17.351 21.878 11.9C21.88 6.43799 17.463 2.01 11.997 2.008C6.54498 2.008 2.11 6.44199 2.108 11.892C2.107 14.117 2.75899 15.783 3.85399 17.526L2.85499 21.174L6.59698 20.193ZM17.984 14.729C17.91 14.605 17.712 14.531 17.414 14.382C17.117 14.233 15.656 13.514 15.383 13.415C15.111 13.316 14.913 13.266 14.714 13.564C14.516 13.861 13.946 14.531 13.773 14.729C13.6 14.927 13.426 14.952 13.129 14.803C12.832 14.654 11.874 14.341 10.739 13.328C9.85598 12.54 9.25798 11.567 9.08598 11.269C8.91298 10.972 9.06798 10.811 9.21598 10.663C9.34998 10.53 9.51298 10.316 9.66198 10.142C9.81298 9.96998 9.86198 9.84598 9.96198 9.64698C10.061 9.44898 10.012 9.27498 9.93698 9.12598C9.86198 8.97798 9.26798 7.51498 9.02098 6.91998C8.77898 6.34099 8.53398 6.41899 8.35198 6.40999L7.78198 6.39999C7.58398 6.39999 7.26198 6.47399 6.98998 6.77199C6.71798 7.06998 5.94999 7.78798 5.94999 9.25098C5.94999 10.714 7.01498 12.127 7.16298 12.325C7.31198 12.523 9.25798 15.525 12.239 16.812C12.948 17.118 13.502 17.301 13.933 17.438C14.645 17.664 15.293 17.632 15.805 17.556C16.376 17.471 17.563 16.837 17.811 16.143C18.059 15.448 18.059 14.853 17.984 14.729Z"
                  fill="#25D366"
                />
              </svg>
              Let us chat with you via WhatsApp
            </label>
            <InfoBubble
              text="Receive quote updates and account confirmations directly to your WhatsApp."
              title="WhatsApp consent information"
              icon-color="#3B4143"
            />
          </div>
        </div>

        <div
          :class="{ '--has-error': $v.agreedToPolicy.$error }"
          class="form-item pre-signup-privacy__confirm"
        >
          <div class="form-checkbox">
            <FormError
              v-if="$v.agreedToPolicy.$error && !$v.agreedToPolicy.sameAs"
              data-testid="privacy-policy-required-error"
              msg="You must agree to the privacy policy"
            />
            <input
              id="privacyPolicy"
              v-model="agreedToPolicy"
              class="form-checkbox__input"
              data-testid="privacy-policy-input"
              name="privacyPolicy"
              type="checkbox"
            />
            <label
              class="form-checkbox__label"
              data-testid="privacy-policy-label"
              for="privacyPolicy"
            >
              By ticking this box you agree to be contacted regarding your
              utilities quote, and agree to our
              <a
                class="policy-link"
                href="https://www.splitthebills.co.uk/privacy-policy/"
                target="_blank"
              >
                privacy policy</a
              >.
            </label>
          </div>
        </div>
      </fieldset>

      <button
        class="btn btn--big btn--full-width form__submit"
        data-testid="submit-pre-signup"
        type="submit"
        @click="trackSubmitBtn"
      >
        Next
      </button>
    </form>

    <div class="marketing-points">
      <div class="marketing-point">
        <div class="icon">
          <House />
        </div>
        <div class="title">
          No Deposits
        </div>
        <div class="subtitle">
          We don’t charge deposits or set up fees.
        </div>
      </div>
      <div class="marketing-point">
        <div class="icon">
          <Star />
        </div>
        <div class="title">
          Highly Rated
        </div>
        <div class="subtitle">
          We’re rated Excellent on Trustpilot.
        </div>
      </div>
      <div class="marketing-point">
        <div class="icon">
          <Lightbulb />
        </div>
        <div class="title">
          Uncapped Energy
        </div>
        <div class="subtitle">
          Never worry about an over-usage bill.
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { required, sameAs } from "vuelidate/lib/validators";
import FormError from "@/components/forms/FormError";
import InfoBubble from "@/components/InfoBubble.vue";
import WarningPanel from "@/components/WarningPanel";
import Lightbulb from "@/assets/svg/lightbulb.svg";
import House from "@/assets/svg/icon-house.svg";
import Star from "@/assets/svg/star.svg";
import { findDiscountCode } from "@/utility/discount";
import EmailInput from "@/components/EmailInput";

export default {
  name: "PreSignup",
  components: {
    EmailInput,
    FormError,
    InfoBubble,
    Lightbulb,
    House,
    Star,
    WarningPanel,
  },
  data() {
    return {
      errorResponse: "",
      agreedToPolicy: false,
    };
  },
  computed: {
    ...mapState(["affiliate", "agent"]),
    leadType: {
      get() {
        return this.$store.getters.leadType;
      },
      set(value) {
        this.$store.commit("SET_LEAD_TYPE", value);
      },
    },
    firstName: {
      get() {
        return this.$store.getters.firstName;
      },
      set(value) {
        this.$store.commit("SET_FIRST_NAME", value);
      },
    },
    lastName: {
      get() {
        return this.$store.getters.lastName;
      },
      set(value) {
        this.$store.commit("SET_LAST_NAME", value);
      },
    },
    emailAddress: {
      get() {
        return this.$store.getters.emailAddress;
      },
      set(value) {
        this.$store.commit("SET_EMAIL_ADDRESS", value);
      },
    },
    telNumber: {
      get() {
        return this.$store.getters.telNumber;
      },
      set(value) {
        this.$store.commit("SET_TEL_NUMBER", value);
      },
    },
    validationErrors() {
      return this.$store.state.validationErrors;
    },
    shouldShowWhatsAppConsent() {
      return this.$store.getters.shouldShowWhatsAppConsent;
    },
    whatsappConsent: {
      get() {
        return this.$store.state.personalDetails.whatsappConsent;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", {
          whatsappConsent: value,
        });
      },
    },
  },
  methods: {
    ...mapActions([
      "getAffiliateMessage",
      "getAgentName",
      "createPreSignupLead",
      "setValidationErrors",
      "clearValidationErrors",
    ]),
    ...mapMutations({
      hideBackButton: "HIDE_BACK_BTN",
      showLoadingPanel: "SHOW_LOADING_PANEL",
      hideLoadingPanel: "HIDE_LOADING_PANEL",
      capitaliseName: "CAPITALISE_NAME",
    }),
    submitForm() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.showLoadingPanel();
        this.errorResponse = "";

        this.capitaliseName();

        this.clearValidationErrors();

        const discountCode = findDiscountCode(this.$route.query, this.$store);

        this.createPreSignupLead({
          agreedToPolicy: this.agreedToPolicy,
          discountCode,
        })
          .then(() => {
            this.$router.push("/household");
          })
          .catch((error) => {
            if (!error.response) {
              this.errorResponse =
                "Oh no! There was a problem with your request, please try again or call us 0330 053 9351";
            } else if (error.response.status === 422) {
              this.setValidationErrors(error.response.data.errors);
            }
          })
          .finally(() => {
            this.hideLoadingPanel();
          });
      }
    },
    trackSubmitBtn() {
      this.trackGoogleEvent("Lead", "Presignup", "Account Capture");
      this.trackGoogleEvent(
        "Lead - " + this.leadType,
        "Presignup",
        "Account Capture",
      );
      this.trackFacebookEvent("Submitted Pre-Signup");
    },
  },
  created() {
    this.hideBackButton();

    this.trackGoogleEvent("Land on Presignup", "Presignup", "Presignup Page");
    this.trackFacebookEvent("Land on Presignup");

    // check for affiliate code in url and validate against endpoint
    let affiliateCode = this.$route.query.aff || localStorage.affiliateCode;
    if (affiliateCode) {
      this.getAffiliateMessage(affiliateCode);
    }

    let agentCode = this.$route.query.agent || localStorage.agentCode;
    if (agentCode) {
      this.getAgentName(agentCode);
    }
  },
  validations() {
    return {
      leadType: {
        required,
      },
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      emailAddress: {
        required,
      },
      telNumber: {
        required,
      },
      agreedToPolicy: {
        sameAs: sameAs(() => true),
      },
    };
  },
};
</script>

<style lang="scss" scoped>
.container {
  max-width: 65rem;
}

.header-text {
  margin-bottom: 3.5rem;

  h1 {
    margin: 0;
  }

  h2 {
    font-size: 17px;
    font-weight: $font-regular;
    margin-top: 0.5rem;
  }
}

.pre-signup-form {
  .pre-signup-privacy__confirm {
    .policy-link {
      font-weight: $font-bold;
    }
  }

  .--has-error {
    .form__radiobuttons label {
      border-color: $error;
    }
  }

  .form__radiobuttons {
    display: flex;

    label {
      cursor: pointer;
      border: 2px solid #e2e2e2;
      border-radius: 10px;
      width: 50%;
      padding: 14px;
      text-align: center;
      display: grid;
      place-items: center;

      &:first-of-type {
        margin-right: 22px;
      }

      &.active {
        border-color: black;
      }

      & input[type="radio"] {
        display: none;
      }
    }
  }

  .whatsapp-consent {
    margin-bottom: 16px;
    margin-left: -24px;
    margin-right: -24px;
    padding: 16px 24px 20px;
    border-radius: 8px;
    background: linear-gradient(
      180deg,
      rgba(37, 211, 102, 0.1) 93.75%,
      rgba(19, 109, 53, 0) 100%
    );
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;

    @include media(">=medium") {
      font-size: 16px;
    }

    &__row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }

    &__label {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      padding-left: calc(4.5rem);
      font-size: 14px;
      margin-right: 8px;
      cursor: pointer;
    }

    &__icon {
      flex-shrink: 0;
      margin-right: 8px;
    }
  }
}

.marketing-points {
  display: flex;
  margin: 4rem 0 0 0;

  @include media("<medium") {
    flex-direction: column;
  }

  .marketing-point {
    display: flex;
    margin: 0 1rem;

    flex-direction: column;
    align-items: center;
    text-align: center;

    @include media("<medium") {
      margin-top: 2rem;
    }

    * {
      margin: 0.5rem 0;
    }

    .icon {
      color: $brand-orange;

      svg {
        height: 3rem;
      }
    }

    .title {
      font-size: 18px;
      font-weight: $font-bold;
      color: $brand-orange;
    }

    .subtitle {
      font-size: 15px;
    }
  }
}
</style>
